
import Vue from "vue";
import moment from "moment";
import debounce from "lodash/debounce";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle";
import TruncatedTextCell from "@/components/shared/TruncatedTextCell";
import AdminDataTable from "@/components/shared/AdminDataTable.vue";
import { JOB_BOARD_TABLES, filterJobBoardRows } from "./jobBoardTables";
import {
  formatHireJobStatus,
  formatInterviewStatus,
  formatJobApplicationStatus,
  formatJobType
} from "@/helpers/adminEnumLabels";

type TableKey =
  | "jobApplications"
  | "jobPosts"
  | "featuredListings"
  | "boostedPosts";

const TABLE_KEYS = JOB_BOARD_TABLES.map(table => table.key) as TableKey[];

const formatRecordDates = (record: any, fields: string[]) => {
  const formatted = { ...record };
  fields.forEach(field => {
    if (formatted[field]) {
      const value = moment(formatted[field]);
      formatted[field] = value.isValid()
        ? value.format("YYYY-MM-DD HH:mm:ss")
        : formatted[field];
    }
  });
  return formatted;
};

export default Vue.extend({
  name: "JobBoard",
  components: { TempStarsTableTitle, TruncatedTextCell, AdminDataTable },
  data() {
    const collections = TABLE_KEYS.reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {} as Record<TableKey, any[]>);
    const queries = TABLE_KEYS.reduce((acc, key) => {
      acc[key] = "";
      return acc;
    }, {} as Record<TableKey, string>);
    return {
      tables: JOB_BOARD_TABLES,
      currentTab: 0,
      jobApplications: [] as any[],
      jobPosts: [] as any[],
      featuredListings: [] as any[],
      boostedPosts: [] as any[],
      originalData: collections,
      searchQueries: queries,
      loading: TABLE_KEYS.reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<TableKey, boolean>),
      debouncedSearchHandlers: {} as Record<TableKey, () => void>
    };
  },
  computed: {
    jobApplicationsHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: "Hire Job ID", value: "hireJobId" },
        { text: "Dentist ID", value: "dentistId" },
        { text: "Hygienist ID", value: "hygienistId" },
        { text: "Resume URL", value: "resumeUrl" },
        { text: "Hire Resume URL", value: "hireResumeUrl" },
        { text: "Resume Viewed", value: "resumeViewed" },
        { text: "Status", value: "status" },
        { text: "Created On", value: "createdOn" },
        { text: "Resume Viewed On", value: "resumeViewedOn" },
        { text: "Interview Status", value: "interviewStatus" },
        { text: "Show Contact", value: "showContact" },
        { text: "Hygienist Resume URL", value: "hygienistResumeUrl" },
        { text: "Hygienist First Name", value: "hygienistFirstName" },
        { text: "Hygienist Last Name", value: "hygienistLastName" }
      ];
    },
    jobPostsHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: "Created On", value: "createdOn" },
        { text: "Dentist ID", value: "dentistId" },
        { text: "Job Type", value: "jobType" },
        { text: "Title", value: "title" },
        { text: "Description", value: "description" },
        { text: "Start Date", value: "startDate" },
        { text: "Approx Start", value: "approxStart" },
        { text: "End Date", value: "endDate" },
        { text: "Approx End", value: "approxEnd" },
        { text: "Days of Week", value: "daysOfWeek" },
        { text: "Send Email To", value: "sendEmailTo" },
        { text: "Is Ortho", value: "isOrtho" },
        { text: "Is Restorative", value: "isRestorative" },
        { text: "Min Years Exp", value: "minYearsExp" },
        { text: "Expires", value: "expires" },
        { text: "Status", value: "status" },
        { text: "Resumes Viewed", value: "resumesViewed" },
        { text: "Paid", value: "paid" },
        { text: "Billing Attempts", value: "billingAttempts" },
        { text: "Deleted On", value: "deletedOn" },
        { text: "Location ID", value: "locationId" },
        { text: "Paid Resumes", value: "paidResumes" },
        { text: "Basis", value: "basis" },
        { text: "Survey Sent", value: "surveySent" }
      ];
    },
    featuredListingsHeaders() {
      return [
        { text: "Feature ID", value: "id", align: "center" },
        { text: "Hire Job ID", value: "hireJobId", align: "center" },
        { text: "Location ID", value: "locationId", align: "center" },
        { text: "Practice Name", value: "practiceName" },
        { text: "Job Type", value: "jobType", align: "center" },
        { text: "Featured On", value: "featuredOn", align: "center" },
        { text: "Expires At", value: "expiresAt", align: "center" },
        { text: "Status", value: "isActive", align: "center", sortable: false },
        { text: "Feature State", value: "featureStatusLabel", align: "center" },
        {
          text: "Has Applications",
          value: "hasApplications",
          align: "center",
          sortable: false
        },
        {
          text: "Charged",
          value: "chargedLabel",
          align: "center",
          sortable: false
        },
        { text: "Amount", value: "amountCharged", align: "center" },
        { text: "Stripe Charge ID", value: "stripeChargeId" }
      ];
    },
    boostedPostsHeaders() {
      return [
        { text: "Boost ID", value: "id", align: "center" },
        { text: "Job ID", value: "jobId", align: "center" },
        { text: "Location ID", value: "locationId", align: "center" },
        { text: "Practice Name", value: "practiceName" },
        { text: "Role", value: "roleType", align: "center" },
        { text: "Dentist Owner Email", value: "dentistOwnerEmail" },
        { text: "Boosted On", value: "boostedOn", align: "center" },
        { text: "Shift Date", value: "jobStartDate", align: "center" },
        { text: "Status", value: "isActive", align: "center", sortable: false },
        { text: "Boost State", value: "boostStatusLabel", align: "center" },
        { text: "Shift State", value: "jobStatusLabel", align: "center" },
        {
          text: "Charged",
          value: "chargedLabel",
          align: "center",
          sortable: false
        },
        { text: "Amount", value: "amountCharged", align: "center" },
        {
          text: "First Offer Received",
          value: "firstOfferReceivedOn",
          align: "center"
        },
        { text: "Stripe Customer ID", value: "stripeCustomerId" },
        { text: "Stripe Charge ID", value: "stripeChargeId" }
      ];
    }
  },
  created() {
    TABLE_KEYS.forEach(key => {
      this.debouncedSearchHandlers[key] = debounce(
        () => this.applySearch(key),
        250
      );
    });
    const requestedTab = Number(this.$route.query.tab);
    if (
      Number.isInteger(requestedTab) &&
      requestedTab >= 0 &&
      requestedTab < TABLE_KEYS.length
    ) {
      this.currentTab = requestedTab;
    }
    this.fetchTable(TABLE_KEYS[this.currentTab]);
  },
  watch: {
    currentTab(tab) {
      const key = TABLE_KEYS[tab];
      if (key && this[key].length === 0) this.fetchTable(key);
    }
  },
  methods: {
    formatHireJobStatus,
    formatInterviewStatus,
    formatJobApplicationStatus,
    formatJobType,
    fetchTable(key: TableKey) {
      const method = `fetch${key.charAt(0).toUpperCase()}${key.slice(1)}`;
      return this[method]();
    },
    applySearch(key: TableKey) {
      this[key] = filterJobBoardRows(
        this.originalData[key],
        this.searchQueries[key]
      );
    },
    handleSearch(key: TableKey) {
      this.debouncedSearchHandlers[key]();
    },
    formatShiftType(type: number | null) {
      if (type === 0) return "Hygienist";
      if (type === 1) return "Assistant";
      return type === null || type === undefined ? "-" : String(type);
    },
    chargeLabel(item: any) {
      if (item.charged !== 1) return "No";
      return item.refunded === 1 ? "Yes (Refunded)" : "Yes";
    },
    async fetchJobApplications() {
      this.loading.jobApplications = true;
      try {
        await this.$store.dispatch("getPaginateData", {
          tableName: "JobApplications",
          limit: 1000,
          page: 1,
          columnNames: this.jobApplicationsHeaders.map(header => header.value)
        });
        const rows = this.$store.state.tableContent || [];
        this.originalData.jobApplications = rows.map(record =>
          formatRecordDates(record, ["createdOn", "resumeViewedOn"])
        );
        this.applySearch("jobApplications");
      } catch (_error) {
        this.notifyError("Error fetching job applications");
      } finally {
        this.loading.jobApplications = false;
      }
    },
    async fetchJobPosts() {
      this.loading.jobPosts = true;
      try {
        const rows = (await this.$store.dispatch("getHireJobs")) || [];
        this.originalData.jobPosts = rows.map(record =>
          formatRecordDates(record, [
            "createdOn",
            "startDate",
            "endDate",
            "expires",
            "deletedOn"
          ])
        );
        this.applySearch("jobPosts");
      } catch (_error) {
        this.notifyError("Error fetching job posts");
      } finally {
        this.loading.jobPosts = false;
      }
    },
    async fetchFeaturedListings() {
      this.loading.featuredListings = true;
      try {
        const rows = (await this.$store.dispatch("getFeaturedListings")) || [];
        this.originalData.featuredListings = rows.map(record =>
          formatRecordDates(record, ["featuredOn", "expiresAt", "refundedOn"])
        );
        this.applySearch("featuredListings");
      } catch (_error) {
        this.notifyError("Error fetching featured listings");
      } finally {
        this.loading.featuredListings = false;
      }
    },
    async fetchBoostedPosts() {
      this.loading.boostedPosts = true;
      try {
        const rows = (await this.$store.dispatch("getBoostedPosts")) || [];
        this.originalData.boostedPosts = rows.map(record =>
          formatRecordDates(record, [
            "boostedOn",
            "firstOfferReceivedOn",
            "receiptSentOn",
            "refundedOn",
            "jobStartDate"
          ])
        );
        this.applySearch("boostedPosts");
      } catch (_error) {
        this.notifyError("Error fetching boosted posts");
      } finally {
        this.loading.boostedPosts = false;
      }
    },
    notifyError(text: string) {
      this.$store.commit("callSnackbar", { text, color: "#FB7672" });
    }
  }
});
